import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Calendar, ClipboardList, Store, Users, Settings,
  QrCode, Bell, MessageSquare, FileText, Menu, X, MoreHorizontal, BarChart2,
  LogOut, UserCircle
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import ThemeToggle from "./ThemeToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import "../styles/command-center.css";

/* ── Nav groups (desktop rail + mobile drawer) ───────────────────────────── */
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/calendar",  icon: Calendar,        label: "Calendar" },
    ],
  },
  {
    label: "Events",
    items: [
      { to: "/events",  icon: Calendar,  label: "Events" },
      { to: "/day",     icon: QrCode,    label: "Check-in" },
    ],
  },
  {
    label: "Reimbursements",
    items: [
      { to: "/claims",        icon: ClipboardList, label: "Claims" },
      { to: "/claims/payments", icon: BarChart2,   label: "Payment runs" },
    ],
  },
  {
    label: "Vendors & billing",
    items: [
      { to: "/vendors",   icon: Store,    label: "Vendors" },
      { to: "/documents", icon: FileText, label: "Documents" },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/users",    icon: Users,         label: "People & roles" },
      { to: "/messages", icon: MessageSquare, label: "Messages" },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
];

const VENDOR_GROUPS = [
  {
    label: "My work",
    items: [
      { to: "/vendor-portal", icon: Store,         label: "My vendor" },
      { to: "/messages",      icon: MessageSquare, label: "Messages" },
    ],
  },
];

/* ── Bottom tabs ─────────────────────────────────────────────────────────── */
const STAFF_TABS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/events",    icon: Calendar,        label: "Events" },
  // slot 2 = DOOR
  { to: "/claims",    icon: ClipboardList,   label: "Claims" },
  { to: "__drawer__", icon: MoreHorizontal,  label: "More" },
];

const VENDOR_TABS = [
  { to: "/vendor-portal", icon: Store,          label: "My vendor" },
  { to: "/messages",      icon: MessageSquare,  label: "Messages" },
  // slot 2 = DOOR
  { to: "/messages",      icon: Bell,           label: "Alerts" },
  { to: "__drawer__",     icon: MoreHorizontal, label: "More" },
];

/* ── Drawer nav item ─────────────────────────────────────────────────────── */
function NavItem({ to, icon: Icon, label, onClick, badge }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm font-medium transition-colors
         ${isActive
           ? "bg-white/10 text-white"
           : "text-white/60 hover:bg-white/8 hover:text-white/90"}`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge ? (
        <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

/* ── Main export ─────────────────────────────────────────────────────────── */
export function Sidebar() {
  const { user, isVendor, logout } = useAuth();
  const { pendingApprovals, unreadNotifications } = useNotifications?.() ?? {};
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef(null);

  const groups = isVendor ? VENDOR_GROUPS : NAV_GROUPS;
  const tabs   = isVendor ? VENDOR_TABS   : STAFF_TABS;

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    function handler(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [drawerOpen]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const badgeFor = (to) => {
    if (to === "/claims"    && pendingApprovals)      return pendingApprovals    > 9 ? "9+" : pendingApprovals    || undefined;
    if (to === "/messages"  && unreadNotifications)   return unreadNotifications > 9 ? "9+" : unreadNotifications || undefined;
    return undefined;
  };

  /* ── Desktop rail (≥860px) — v2 icon rail ─────────────────────────────── */
  const railGroups = groups.filter((g) => g.label !== "System");
  const systemItems = groups.filter((g) => g.label === "System").flatMap((g) => g.items);
  const initialsOf = (name = "") =>
    name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";

  const railLink = ({ to, icon: Icon, label }) => (
    <NavLink
      key={to + label}
      to={to}
      aria-label={label}
      className={({ isActive }) => `ivcc-rn${isActive ? " on" : ""}`}
      data-testid={`rail-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <Icon strokeWidth={1.8} />
      {badgeFor(to) ? <span className="ivcc-rn-badge" /> : null}
      <span className="ivcc-tip">{label}{badgeFor(to) ? ` · ${badgeFor(to)}` : ""}</span>
    </NavLink>
  );

  const desktopRail = (
    <aside className="ivcc ivcc-rail hidden app:flex" data-testid="desktop-rail">
      <NavLink to={isVendor ? "/vendor-portal" : "/dashboard"} className="ivcc-seal" aria-label="Home">
        <img src="/iv-logo.png" alt="Ismaili Volunteers" />
      </NavLink>
      <div className="ivcc-rail-div" />

      <nav className="ivcc-rail-nav">
        {railGroups.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && <div className="ivcc-rail-div" />}
            {group.items.map(railLink)}
          </React.Fragment>
        ))}
      </nav>

      <div className="ivcc-rail-bot">
        <div className="ivcc-rail-div" />
        <ThemeToggle variant="rail" />
        {systemItems.length > 0 && <div className="ivcc-rail-div" />}
        {systemItems.map(railLink)}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="ivcc-av" title={user?.name ?? "Account"} aria-label="Account menu">
              {initialsOf(user?.name)}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-52">
            <div className="px-3 py-2">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{user?.role?.replace(/_/g, " ")}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <UserCircle className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="px-3 py-2">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Theme
              </p>
              <ThemeToggle />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => { logout(); navigate("/login"); }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );

  /* ── Mobile top bar ───────────────────────────────────────────────────── */
  const mobileTopBar = (
    <header className="app:hidden fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between bg-nav-rail px-4">
      <img src="/iv-logo.png" alt="Ismaili Volunteers" className="h-8 w-8 rounded-full object-cover" />
      <button
        onClick={() => setDrawerOpen(true)}
        aria-label="Open menu"
        className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white"
      >
        <Menu className="h-[22px] w-[22px]" />
      </button>
    </header>
  );

  /* ── Mobile drawer ────────────────────────────────────────────────────── */
  const mobileDrawer = (
    <>
      {drawerOpen && (
        <div
          className="app:hidden fixed inset-0 z-50 bg-[rgba(3,7,13,.6)]"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div
        ref={drawerRef}
        className={`app:hidden fixed inset-y-0 left-0 z-50 flex flex-col bg-nav-rail
          transition-transform duration-[240ms] ease-[cubic-bezier(.22,1,.36,1)]
          ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ width: "min(302px, 86vw)" }}
        aria-hidden={!drawerOpen}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <img src="/iv-logo.png" alt="Ismaili Volunteers" className="h-7 w-7 rounded-full object-cover" />
            <span className="font-heading text-sm font-bold text-white">Ismaili Volunteers</span>
          </div>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="rounded-md p-2 text-white/60 hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-semibold uppercase shrink-0">
            {user?.name?.[0] ?? "?"}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{user?.name ?? "User"}</p>
            <p className="truncate text-xs text-white/50 capitalize">{user?.role?.replace(/_/g, " ") ?? ""}</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map(({ to, icon, label }) => (
                  <NavItem
                    key={to + label}
                    to={to}
                    icon={icon}
                    label={label}
                    badge={badgeFor(to)}
                    onClick={() => setDrawerOpen(false)}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-white/10 px-4 py-3">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/30">
            Theme
          </p>
          <ThemeToggle variant="panel" />
        </div>
      </div>
    </>
  );

  /* ── Bottom tab bar ───────────────────────────────────────────────────── */
  const bottomBar = (
    <nav
      className="app:hidden fixed inset-x-0 bottom-0 z-40 flex items-end bg-nav-rail border-t border-white/10"
      style={{ paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))" }}
    >
      {tabs.map((tab, i) => {
        if (i === 2) {
          return (
            <React.Fragment key="door-slot">
              <div className="relative flex flex-1 justify-center">
                <NavLink
                  to="/day"
                  aria-label="Check-in (DOOR)"
                  className="absolute -top-4 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-primary ring-[3px] ring-nav-rail shadow-door transition-transform active:scale-95"
                >
                  <QrCode className="h-7 w-7 text-white" />
                </NavLink>
                <div className="h-12 w-[60px]" />
              </div>
              <BottomTab tab={tab} badgeFor={badgeFor} onOpenDrawer={() => setDrawerOpen(true)} />
            </React.Fragment>
          );
        }
        return <BottomTab key={tab.to + i} tab={tab} badgeFor={badgeFor} onOpenDrawer={() => setDrawerOpen(true)} />;
      })}
    </nav>
  );

  return (
    <>
      {desktopRail}
      {mobileTopBar}
      {mobileDrawer}
      {bottomBar}
    </>
  );
}

function BottomTab({ tab, badgeFor, onOpenDrawer }) {
  const Icon = tab.icon;
  const badge = badgeFor(tab.to);
  const tabClass = (isActive) =>
    `relative flex flex-1 flex-col items-center gap-0.5 pt-2 pb-1 text-[10px] font-medium transition-colors
     ${isActive ? "text-[#7cc3ea]" : "text-[#6b87a3] hover:text-white/70"}`;

  // "More" has no page of its own — it opens the same drawer as the menu button.
  if (tab.to === "__drawer__") {
    return (
      <button type="button" onClick={onOpenDrawer} className={tabClass(false)} aria-label="More">
        <span className="relative flex items-center justify-center rounded-full px-3 py-1">
          <Icon className="h-5 w-5" />
        </span>
        <span>{tab.label}</span>
      </button>
    );
  }

  return (
    <NavLink
      to={tab.to}
      className={({ isActive }) => tabClass(isActive)}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute top-0 inset-x-2 h-[3px] rounded-b-full bg-[#7cc3ea]" />
          )}
          <span className={`relative flex items-center justify-center rounded-full px-3 py-1 transition-colors
            ${isActive ? "bg-[rgba(77,160,207,.16)]" : ""}`}>
            <Icon className="h-5 w-5" />
            {badge && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold text-white">
                {badge}
              </span>
            )}
          </span>
          <span>{tab.label}</span>
        </>
      )}
    </NavLink>
  );
}
